import { apiRoutes, brands, DEFAULT_BRAND_VALUE, searchNavigationParams } from "../constants/constants"
import {
  campaignTypes,
  fhPages,
  fhParams,
  fhPatterns,
  fhViews,
  itemsPerPage,
  queries,
  searchDefaults,
  selectors,
  suppressors,
  tabs,
} from "../constants/search-constants"
import { ApiService } from "../lib/ApiService"
import { Brand, NavigationParam, Page, QueryType, SearchQueryParams, Suppressor, Tab } from "../types/global"
import antiXss from "./anti-xss"
import { replaceSymbol } from "./common-utils"
import { extractCountryId, getFHLocaleFormat } from "./locale-utils"
import { specialCharactersToUnicode } from "./search-term-utils"

export const getFhLocationParam = (
  navigationParam: NavigationParam,
  searchServiceLocale: string,
  countryId: string,
  selectedTab: Tab,
  brand: Brand,
  queryType: QueryType,
  urlParams: string = null
): string => {
  const defaultFhLocationSelector = getDefaultFhLocationSelector(searchServiceLocale, brand.value)
  /* If user has clicked an element on the interface reuse the url_params attribute from Fredhopper */
  if (urlParams) {
    const urlParamsSelectors = getSelectorsFromUrlParams(urlParams, selectedTab, queryType)
    return `${defaultFhLocationSelector}${urlParamsSelectors}`
  }

  const typeSelector = getTypeSelector(queryType, selectedTab)
  const categoriesSelector = getCategoriesSelector(navigationParam, countryId, brand)
  const specificSelector = getSpecificSelector(navigationParam, searchServiceLocale)

  return `${defaultFhLocationSelector}${typeSelector}${categoriesSelector}${specificSelector}`
}

/* Default location selector, the part that is always the same for all queries */
const getDefaultFhLocationSelector = (searchServiceLocale: string, brandValue: string): string => {
  return `//${searchDefaults.UNIVERSE}/${searchServiceLocale}/${selectors.BRAND_SELECTOR}>{${brandValue}}/${selectors.STATUS_SELECTOR}=${searchDefaults.STATUS_SELECTOR}`
}

/* Helper method to get the type selector based on type of query and selected tab */
const getTypeSelector = (queryType: QueryType, selectedTab: Tab): string => {
  return queryType === queries.SEARCH ? `/${selectors.TYPE_SELECTOR}=${selectedTab}` : ""
}

/* Helper method to determine the specific selector based on user navigation choice */
const getSpecificSelector = (navigationParam: NavigationParam, searchServiceLocale: string): string => {
  if (!navigationParam) return ""

  let specificSelector = ""

  switch (navigationParam.name) {
    case searchNavigationParams.SEARCH_TERM:
      const sanitizedSearchTerm = antiXss(specialCharactersToUnicode(<string>navigationParam.value))
      specificSelector = `${selectors.SEARCH_TERM}=${sanitizedSearchTerm}`
      break
    case searchNavigationParams.NEW:
      specificSelector = `${selectors.NEW}_${searchServiceLocale.toLowerCase()}>{${navigationParam.value}}`
      break
    case searchNavigationParams.CHARACTERISTICS:
      specificSelector = `${selectors.CHARACTERISTICS}>{${replaceSymbol(
        <string>navigationParam.value,
        "_",
        ""
      ).toLowerCase()}}`
      break
    case searchNavigationParams.APPLICATION_PURPOSES:
      specificSelector = `${selectors.APPLICATION_PURPOSES}>{${replaceSymbol(
        <string>navigationParam.value,
        "_",
        ""
      ).toLowerCase()}}`
      break
    case searchNavigationParams.CROSS:
      specificSelector = `${selectors.CROSS}_${searchServiceLocale.toLowerCase()}>{true}`
      break
    default:
      break
  }

  return specificSelector ? `/${specificSelector}` : ""
}

/* Helper method to determine the category tree selected from FH and to ecreate the categories selector */
const getCategoriesSelector = (navigationParam: NavigationParam, countryId: string, brand: Brand) => {
  /* Brand specific category suffix, no suffix for default brand */
  const brandSuffix = brand.value === DEFAULT_BRAND_VALUE ? "" : `x${brand.value}`

  /* Parse the brand specific secondary serie category in Fredhopper friendly format */
  const brandSecondarySerieCategory = replaceSymbol(brand.seriesRootCategory.toLowerCase(), "_", "")

  /* Check if we should use the Systems or the Series category tree. The default tree is the Systems one */
  const defaultCategory =
    navigationParam?.name === searchNavigationParams.SERIES
      ? `${searchDefaults.UNIVERSE}_${searchDefaults.SERIE_FH}`
      : `${searchDefaults.UNIVERSE}_${searchDefaults.SYSTEM_FH}`

  /* Default parameter to get the root category of the tree */
  const defaultCategoriesSelector = `${selectors.CATEGORIES}<{${defaultCategory}x${countryId}${brandSuffix}`

  /* The return value is the default selection from the Systems category tree
    when the navigation choice is neither 'systems' or 'series' */
  let categoriesSelector = defaultCategoriesSelector

  /* If navigation choice is 'systems' or 'series' navigation then we have array with category IDs
    to pass as parameter to FH and create a selector from the chosen category tree */
  if (
    navigationParam?.name === searchNavigationParams.SYSTEMS ||
    navigationParam?.name === searchNavigationParams.SERIES
  ) {
    /* Array with the IDs of the selected categories, ordered top down by level */
    const selectedCategories = <Array<string>>navigationParam.value

    /* Gather all the category parameters in an array */
    const categoryParams = [categoriesSelector]
    selectedCategories.forEach((categoryId, index) => {
      /* If the chosen category tree is the 'series' one there is one additional default category
        use it to initialize the first category selection from the series tree
        and don't add it to array for the selection, we don't need it */
      const baseCategory =
        navigationParam.name === searchNavigationParams.SERIES && index === 0
          ? `${defaultCategoriesSelector}_${brandSecondarySerieCategory}x${countryId}${brandSuffix}`
          : categoryParams[index]
      const param = `${baseCategory}_${replaceSymbol(categoryId.toLowerCase(), "_", "")}x${countryId}${brandSuffix}`
      categoryParams.push(param)
    })

    /* Concatenate the parameters in FH format */
    categoriesSelector = categoryParams.join("}/")
  }

  /* After all the selected category levels are added to the query string, put the final closing bracket */
  categoriesSelector += "}"

  return `/${categoriesSelector}`
}

/* Helper method to reuse the url_params from Fredhopper.
  For meta queries remove the type_selector, for search queries add it to the selectors. */
const getSelectorsFromUrlParams = (urlParams: string, selectedTab: Tab, queryType: QueryType): string => {
  let fhLocationFromFredhopper = ""
  const typeSelector = `${selectors.TYPE_SELECTOR}=${selectedTab}`

  if (queryType === queries.META) {
    fhLocationFromFredhopper = urlParams.includes(fhPatterns.TYPE_SELECTOR)
      ? urlParams.replace(new RegExp(`/${typeSelector}|${typeSelector}/`), "")
      : urlParams
  } else if (queryType === queries.SEARCH) {
    fhLocationFromFredhopper = urlParams.includes(fhPatterns.TYPE_SELECTOR) ? urlParams : `${typeSelector}/${urlParams}`
  }
  return `/${fhLocationFromFredhopper}`
}

/* END OF HELPERS FOR fh_location PARAMETER */

/* Return the appropriate page parameter value based on user navigation */
export const getPageParam = (navigationParam: NavigationParam, urlParams?: string): Page => {
  //return null if page param is already contained in the urlParams coming from FH
  if (urlParams && urlParams.search(/\bpage=/gi) !== -1) return null
  switch (navigationParam?.name) {
    case searchNavigationParams.NEW:
      return fhPages.NOVELTIES
    case searchNavigationParams.CROSS:
      return fhPages.CROSS
    case searchNavigationParams.MIX_AND_MATCH:
      return fhPages.MIX_AND_MATCH
    case searchNavigationParams.ALL_SPARE_PARTS:
      return fhPages.SPARE_PARTS
    default:
      return null
  }
}

/* Return the appropriate suppressor to send to FH based on type of query */
export const getFhSuppressParam = (queryType: QueryType, hasFacets = false): Suppressor => {
  if (queryType === queries.META) return suppressors.ITEMS
  if (queryType === queries.SEARCH && !hasFacets) return suppressors.FACETS
  return null
}

/* Helper method to determine the start index for the next page result query */
export const getFhStartIndexParam = (pageNumber: number, selectedTab: Tab): string => {
  return String((pageNumber - 1) * itemsPerPage[selectedTab])
}

/* Helper method to determine the ranking parameter for system navigation lister pages.
  Applied only for search queries for products. */
export const getSystemDisplayRankingParam = (
  navigationParam: NavigationParam,
  selectedTab: Tab,
  queryType: QueryType,
  countryId: string
): string => {
  if (
    navigationParam.name === searchNavigationParams.SYSTEMS &&
    selectedTab === tabs.PRO &&
    queryType === queries.SEARCH
  ) {
    const levelOneSystemCategory = navigationParam.value[0]
    return `${replaceSymbol(levelOneSystemCategory.toLowerCase(), "_", "")}x${countryId}`
  }

  return null
}

/* Helper method to determine the ranking parameter for series navigation lister pages.
  Applied only for search queries for products. */
export const getSeriesDisplayRankingParam = (
  navigationParam: NavigationParam,
  selectedTab: Tab,
  queryType: QueryType,
  countryId: string
): string => {
  if (
    navigationParam.name === searchNavigationParams.SERIES &&
    selectedTab === tabs.PRO &&
    queryType === queries.SEARCH
  ) {
    const levelOneSeriesCategory = navigationParam.value[0]
    return `${replaceSymbol(levelOneSeriesCategory.toLowerCase(), "_", "")}x${countryId}`
  }

  return null
}

/* Helper method to construct the products parameter expected by FH from an array of product ids */
export const getProductsParam = (products: Array<string>): string => {
  return products.map((p) => p.toLocaleLowerCase()).join(";")
}

/* Helper method to construct the fh_secondid parameter expected by FH */
export const getFhSecondId = (id: string): string => {
  return "p_" + id.toLocaleLowerCase()
}

/* Partial search query, it is a separate case used only for the partial results view */
export const getPartialSearchQueryUri = ({
  searchTerm,
  searchServiceLocale,
  countryId,
  selectedTab,
  brandValue,
}: {
  searchTerm: string
  searchServiceLocale: string
  countryId: string
  selectedTab: Tab
  brandValue: string
}) => {
  /* Brand specific category suffix, no suffix for default brand */
  const brandSuffix = brandValue === DEFAULT_BRAND_VALUE ? "" : `x${brandValue}`

  const defaultFhLocationSelector = getDefaultFhLocationSelector(searchServiceLocale, brandValue)
  const typeSelector = `/${selectors.TYPE_SELECTOR}=${selectedTab}`
  const categoriesSelector = `/${selectors.CATEGORIES}<{${searchDefaults.UNIVERSE}_${searchDefaults.SYSTEM_FH}x${countryId}${brandSuffix}}`
  const specificSelector = `/${selectors.SEARCH_TERM}=${specialCharactersToUnicode(searchTerm)}`

  const fhLocation = `${defaultFhLocationSelector}${typeSelector}${categoriesSelector}${specificSelector}`

  return `${apiRoutes.SEARCH}?${fhParams.LOCATION}=${fhLocation}&${fhParams.VIEW_SIZE}=${itemsPerPage.PARTIAL_RESULTS}`
}

/* Helper to create the search query URI based on parameters passed. Use when querying from the frontend as it contains the relative api route.
  For quering from the server use the open api client directly. */
export const getSearchQueryUri = ({
  fhLocation,
  fhView,
  fhViewSize,
  fhStartIndex,
  fhSuppress,
  fhSecondId,
  systemDisplayRanking,
  seriesDisplayRanking,
  page,
  products,
}: SearchQueryParams): string => {
  let queryParams = `${fhParams.LOCATION}=${fhLocation}`
  queryParams += systemDisplayRanking ? `&${fhParams.SYSTEM_DISPLAY_RANKING}=${systemDisplayRanking}` : ""
  queryParams += seriesDisplayRanking ? `&${fhParams.SERIES_DISPLAY_RANKING}=${seriesDisplayRanking}` : ""
  queryParams += fhView ? `&${fhParams.VIEW}=${fhView}` : ""
  queryParams += fhViewSize ? `&${fhParams.VIEW_SIZE}=${fhViewSize}` : ""
  queryParams += fhStartIndex ? `&${fhParams.START_INDEX}=${fhStartIndex}` : ""
  queryParams += fhSuppress ? `&${fhParams.SUPPRESS}=${fhSuppress}` : ""
  queryParams += fhSecondId ? `&${fhParams.SECOND_ID}=${fhSecondId}` : ""
  queryParams += page ? `&${fhParams.PAGE}=${page}` : ""
  queryParams += products ? `&${fhParams.PRODUCTS}=${products}` : ""

  return `${apiRoutes.SEARCH}?${queryParams}`
}

/* Helpers for building different Fredhopper queries */

/* Get canBeCombinedWith products for the PDP page carousel */
export const getPdpCombineWith = (
  locale: string,
  brand: string,
  combineWithProductIDs: Array<string>,
  productId: string
) => {
  /* Construct FH query for list of canBeCombinedWith products */
  const searchServiceLocale = getFHLocaleFormat(locale)
  const selectedTab = tabs.PRO
  const countryId = extractCountryId(locale)

  const fhLocation = getFhLocationParam(null, searchServiceLocale, countryId, selectedTab, brands[brand], queries.META)
  const fhView = fhViews.DETAIL // This is the required by FH value for this type of carousel
  const fhSecondid = getFhSecondId(productId)
  const products = getProductsParam(combineWithProductIDs)
  const fhSuppress = `${suppressors.ITEMS},${suppressors.FACETS}` // Suppress as much as possible to lower the response size
  const campaignType = campaignTypes.PDP_COMBINABLE // Pass the proper parameter for this campaign

  const { searchApi: api } = ApiService()

  return api.getFredhopperQuery({
    fhLocation,
    products,
    fhSecondid,
    fhView,
    fhSuppress,
    campaignType,
  })
}

/* Get accessories products for the PDP page carousel */
export const getPdpAccessories = (
  locale: string,
  brand: string,
  accessoriesProductIDs: Array<string>,
  productId: string
) => {
  /* Construct FH query for list of canBeCombinedWith products */
  const searchServiceLocale = getFHLocaleFormat(locale)
  const selectedTab = tabs.PRO
  const countryId = extractCountryId(locale)

  const fhLocation = getFhLocationParam(null, searchServiceLocale, countryId, selectedTab, brands[brand], queries.META)
  const fhView = fhViews.DETAIL // This is the required by FH value for this type of carousel
  const fhSecondid = getFhSecondId(productId)
  const products = getProductsParam(accessoriesProductIDs)
  const fhSuppress = `${suppressors.ITEMS},${suppressors.FACETS}` // Suppress as much as possible to lower the response size
  const campaignType = campaignTypes.PDP_ACCESSORIES // Pass the proper parameter for this campaign

  const { searchApi: api } = ApiService()

  return api.getFredhopperQuery({
    fhLocation,
    products,
    fhSecondid,
    fhView,
    fhSuppress,
    campaignType,
  })
}

/* Get last seen items for the homepage carousel */
export const getHomepageLastSeen = (locale: string, brand: string, products: string) => {
  /* Construct FH query for list of last seen items */
  const searchServiceLocale = getFHLocaleFormat(locale)
  const selectedTab = tabs.PRO
  const countryId = extractCountryId(locale)

  const fhLocation = getFhLocationParam(null, searchServiceLocale, countryId, selectedTab, brands[brand], queries.META)
  const fhView = fhViews.HOME // This is the required by FH value for this type of carousel
  const fhSuppress = `${suppressors.ITEMS},${suppressors.FACETS}` // Suppress as much as possible to lower the response size
  const campaignType = campaignTypes.HOME_LAST_SEEN // Pass the proper parameter for this campaign

  const { searchApi: api } = ApiService()

  return api.getFredhopperQuery({
    fhLocation,
    products,
    fhView,
    fhSuppress,
    campaignType,
  })
}

/* Get novelties products for the homepage carousel */
export const getHomepageNovelties = (locale: string, brand: string) => {
  /* Construct FH query for list of novelties products */
  const searchServiceLocale = getFHLocaleFormat(locale)
  const selectedTab = tabs.PRO
  const countryId = extractCountryId(locale)

  const fhLocation = getFhLocationParam(
    null,
    searchServiceLocale,
    countryId,
    selectedTab,
    brands[brand],
    queries.SEARCH
  )
  const fhView = fhViews.HOME // This is the required by FH value for this type of carousel
  const fhSuppress = `${suppressors.ITEMS},${suppressors.FACETS}` // Suppress as much as possible to lower the response size
  const campaignType = campaignTypes.HOME_NOVELTIES // Pass the proper parameter for this campaign

  const { searchApi: api } = ApiService()

  return api.getFredhopperQuery({
    fhLocation,
    fhView,
    fhSuppress,
    campaignType,
  })
}

/* Get further products for the PDP page carousel */
export const getPdpFurtherProducts = (locale: string, brand: string, productId: string) => {
  /* Construct FH query for list of further products */
  const searchServiceLocale = getFHLocaleFormat(locale)
  const selectedTab = tabs.PRO
  const countryId = extractCountryId(locale)

  const fhLocation = getFhLocationParam(
    null,
    searchServiceLocale,
    countryId,
    selectedTab,
    brands[brand],
    queries.SEARCH
  )
  const fhView = fhViews.DETAIL // This is the required by FH value for this type of carousel
  const fhSecondid = getFhSecondId(productId)
  const fhSuppress = `${suppressors.ITEMS},${suppressors.FACETS}` // Suppress as much as possible to lower the response size
  const campaignType = campaignTypes.PDP_FURTHER_PRODUCTS // Pass the proper parameter for this campaign

  const { searchApi: api } = ApiService()

  return api.getFredhopperQuery({
    fhLocation,
    fhSecondid,
    fhView,
    fhSuppress,
    campaignType,
  })
}

/* Get Fredhopper meta response from API */
export const getFhMeta = (
  queryParam: NavigationParam,
  locale: string,
  brand: string,
  selectedTab: Tab,
  urlParams: string = null
) => {
  /* Construct FH query for meta response */
  const searchServiceLocale = getFHLocaleFormat(locale)
  const countryId = extractCountryId(locale)

  const fhLocation = getFhLocationParam(
    queryParam,
    searchServiceLocale,
    countryId,
    selectedTab,
    brands[brand],
    queries.META,
    urlParams
  )

  const fhSuppress = getFhSuppressParam(queries.META)
  const page = getPageParam(queryParam, urlParams)

  const { searchApi: api } = ApiService()

  return api.getFredhopperQuery({
    fhLocation,
    fhSuppress,
    page,
  })
}

/* Get Fredhopper search results response from API */
export const getFhSearchResults = (
  queryParam: NavigationParam,
  locale: string,
  brand: string,
  selectedTab: Tab,
  urlParams: string = null,
  shouldReturnAll = false,
  pageNumber: number = searchDefaults.START_PAGE
) => {
  /* Construct FH query for search results */
  const searchServiceLocale = getFHLocaleFormat(locale)
  const countryId = extractCountryId(locale)

  const fhLocation = getFhLocationParam(
    queryParam,
    searchServiceLocale,
    countryId,
    selectedTab,
    brands[brand],
    queries.SEARCH,
    urlParams
  )

  const fhSuppress = getFhSuppressParam(queries.SEARCH, true)
  const page = getPageParam(queryParam, urlParams)
  const systemDisplayRanking = getSystemDisplayRankingParam(queryParam, selectedTab, queries.SEARCH, countryId)
  const seriesDisplayRanking = getSeriesDisplayRankingParam(queryParam, selectedTab, queries.SEARCH, countryId)
  const fhViewSize = shouldReturnAll ? String(itemsPerPage.CATALOG) : String(itemsPerPage[selectedTab])
  const fhStartIndex = getFhStartIndexParam(pageNumber, selectedTab)

  const { searchApi: api } = ApiService()

  return api.getFredhopperQuery({
    fhLocation,
    fhSuppress,
    page,
    systemDisplayRanking,
    seriesDisplayRanking,
    fhViewSize,
    fhStartIndex,
  })
}
